import React, { useEffect, useMemo, useState } from 'react';
import * as Inovx from '@shared';
import { ExternalSourceCandidate } from './types';
import { loadExternalAttributeSourcesForFacility } from './externalAttributesDataSourceLoader';
import ExternalDataSourcePicker from './ExternalDataSourcePicker';
import LinkedExternalSourceList, { LinkedExternalSourceGroup } from './LinkedExternalSourceList';

const SystemActions = Inovx.Shared.System.Actions;

export interface ExternalAttributesPanelProps {
    assetClassID: string;
    links: IExternalDataSourceLink[];
    definitions: IExternalAttributeDefinition[];
    matchTargetAttributes: IAttributeDefinition[];
    facilities: IFacility[];
    onLinksChange: (links: IExternalDataSourceLink[]) => void;
    onDefinitionsChange: (definitions: IExternalAttributeDefinition[]) => void;
}

const ExternalAttributesPanel: React.FC<ExternalAttributesPanelProps> = ({
    assetClassID,
    links,
    definitions,
    matchTargetAttributes,
    facilities,
    onLinksChange,
    onDefinitionsChange,
}) => {
    const [sources, setSources] = useState<ExternalSourceCandidate[]>([]);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [expandedLinkId, setExpandedLinkId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [selectedFacilityId, setSelectedFacilityId] = useState<string>('');

    useEffect(() => {
        setSelectedFacilityId(current => {
            if (current && facilities.some(facility => facility.ID === current)) {
                return current;
            }

            return '';
        });
    }, [facilities]);

    const selectedFacility = useMemo(() => {
        return facilities.find(facility => facility.ID === selectedFacilityId) || null;
    }, [facilities, selectedFacilityId]);

    useEffect(() => {
        let active = true;

        setSources([]);
        setLoadError(null);
        setExpandedLinkId(null);

        if (!selectedFacility) {
            setLoading(false);
            return () => {
                active = false;
            };
        }

        setLoading(true);
        loadExternalAttributeSourcesForFacility(selectedFacility)
            .then(result => {
                if (!active) {
                    return;
                }

                setSources(result.Sources);
                setLoadError(result.Error || null);
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [assetClassID, selectedFacility]);

    const createLinkId = () => {
        if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
            return crypto.randomUUID();
        }

        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
            const value = Math.floor(Math.random() * 16);
            return (char === 'x' ? value : (value & 0x3) | 0x8).toString(16);
        });
    };

    const visibleLinks = useMemo(() => {
        return links.filter(link => link.FacilityID === selectedFacilityId);
    }, [links, selectedFacilityId]);
    const visibleLinkIds = useMemo(() => new Set(visibleLinks.map(link => link.ID)), [visibleLinks]);
    const visibleDefinitions = useMemo(() => {
        return definitions.filter(definition => visibleLinkIds.has(definition.ExternalDataSourceLinkID));
    }, [definitions, visibleLinkIds]);
    const linkedSourceIds = useMemo(() => {
        return visibleLinks.map(link => link.DataSourceID);
    }, [visibleLinks]);
    const linkedSourceGroups = useMemo<LinkedExternalSourceGroup[]>(() => {
        const sourceById = new Map(sources.map(source => [source.ID, source]));
        return visibleLinks.map((link) => {
            const sourceDefinitions = definitions.filter((definition) => definition.ExternalDataSourceLinkID === link.ID);
            return {
                link,
                source: sourceById.get(link.DataSourceID),
                definitions: sourceDefinitions,
                draftOnly: sourceDefinitions.length === 0,
            };
        });
    }, [definitions, sources, visibleLinks]);

    const linkSources = (selectedSources: ExternalSourceCandidate[]) => {
        if (!selectedFacility) {
            setPickerOpen(false);
            return;
        }

        const existingSourceIds = new Set(visibleLinks.map(link => link.DataSourceID));
        const newLinks = selectedSources
            .filter(source => !existingSourceIds.has(source.ID))
            .map(source => ({
                ID: createLinkId(),
                FacilityID: selectedFacility.ID,
                DataSourceID: source.ID,
                DisplayName: source.Name,
            }));

        if (newLinks.length > 0) {
            onLinksChange([...links, ...newLinks]);
            setExpandedLinkId(newLinks[0].ID);
        }

        setPickerOpen(false);
    };

    const removeLink = (linkId: string) => {
        SystemActions.displayConfirmationMessage({
            message: InovxLoader.t('facility:externalAttributes.confirmUnlink'),
            confirmCallback: () => {
                onLinksChange(links.filter(link => link.ID !== linkId));
                setExpandedLinkId(current => current === linkId ? null : current);
                onDefinitionsChange(definitions.filter((definition) => definition.ExternalDataSourceLinkID !== linkId));
            },
        });
    };

    const updateLink = (link: IExternalDataSourceLink) => {
        onLinksChange(links.map(item => item.ID === link.ID ? link : item));
    };

    const toggleExpanded = (linkId: string) => {
        setExpandedLinkId(current => current === linkId ? null : linkId);
    };

    const updateSourceDefinitions = (linkId: string, sourceDefinitions: IExternalAttributeDefinition[]) => {
        const otherDefinitions = definitions.filter((definition) => definition.ExternalDataSourceLinkID !== linkId);
        onDefinitionsChange([...otherDefinitions, ...sourceDefinitions]);
    };

    const duplicateDisplayNames = useMemo(() => {
        const counts = new Map<string, number>();
        visibleLinks.forEach(link => {
            const key = link.DisplayName.trim().toLocaleLowerCase();
            if (key) {
                counts.set(key, (counts.get(key) || 0) + 1);
            }
        });
        return new Set(visibleLinks
            .filter(link => {
                const key = link.DisplayName.trim().toLocaleLowerCase();
                return !!key && (counts.get(key) || 0) > 1;
            })
            .map(link => link.ID));
    }, [visibleLinks]);

    const linkDisabled = loading || !selectedFacility;

    return (
        <div className="configuration-form-tab-pane external-attributes-panel">
            <div className="row">
                <div className="col-md-12">
                    <div className="external-attributes-facility-selector">
                        <div className="external-attributes-facility-prompt">
                            {InovxLoader.t('facility:externalAttributes.facilityPrompt')}
                        </div>
                        <select
                            className="form-control external-attributes-facility-select"
                            value={selectedFacilityId}
                            disabled={facilities.length === 0}
                            aria-label={InovxLoader.t('facility:externalAttributes.selectFacility')}
                            onChange={event => setSelectedFacilityId(event.target.value)}
                        >
                            <option value="">{InovxLoader.t('facility:externalAttributes.selectFacility')}</option>
                            {facilities.map(facility => (
                                <option key={facility.ID} value={facility.ID}>{facility.Name}</option>
                            ))}
                        </select>
                    </div>
                    {facilities.length === 0 && (
                        <div className="external-attributes-warning">
                            {InovxLoader.t('facility:externalAttributes.noEligibleFacilities')}
                        </div>
                    )}
                    {selectedFacility && (
                        <button
                            type="button"
                            className="configuration-form-add-primary-btn plus-btn external-attributes-link-button"
                            onClick={() => setPickerOpen(true)}
                            disabled={linkDisabled}
                            title={InovxLoader.t('facility:externalAttributes.linkAction')}
                        >
                            <span className="plus-btn-icon"><i className="fa fa-plus" /></span>
                            <div className="configuration-form-panel-title">{InovxLoader.t('facility:externalAttributes.linkAction')}</div>
                        </button>
                    )}
                </div>
            </div>

            <div className="external-attributes-selected-content">
                {selectedFacility && loading && (
                    <div className="external-attributes-loading">{InovxLoader.t('facility:externalAttributes.loading')}</div>
                )}

                {selectedFacility && loadError && (
                    <div className="external-attributes-load-failures">
                        {InovxLoader.t('facility:externalAttributes.loadFailure')}
                    </div>
                )}

                {selectedFacility && (
                    <LinkedExternalSourceList
                        assetClassID={assetClassID}
                        groups={linkedSourceGroups}
                        allDefinitions={visibleDefinitions}
                        matchTargetAttributes={matchTargetAttributes}
                        expandedLinkId={expandedLinkId}
                        duplicateDisplayNameLinkIds={duplicateDisplayNames}
                        onToggleExpanded={toggleExpanded}
                        onRemove={removeLink}
                        onLinkChange={updateLink}
                        onSourceDefinitionsChange={updateSourceDefinitions}
                    />
                )}
            </div>

            <ExternalDataSourcePicker
                visible={pickerOpen}
                sources={sources}
                linkedSourceIds={linkedSourceIds}
                onAdd={linkSources}
                onClose={() => setPickerOpen(false)}
            />
        </div>
    );
};

export default ExternalAttributesPanel;
